import React, { useState } from 'react';
import { Alert, Form, Input, Modal, Space, Typography, notification } from 'antd';
import { campaignsApi } from '../services/catalogServiceAPI/campaignsProxy';
import { parseList } from '../utils/parseList';

const { TextArea } = Input;

const firstBlock = (content, type) => content?.blocks?.find((b) => b.type === type);

/**
 * Delivers the campaign's dialog as an inbox notification to a few users:
 * the way to see it on a real device before it goes live.
 */

/** Notification rows are plain text: the dialog's inline markdown (bold, italic, links) is dropped. */
const plainText = (md) => md
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .trim();

export function SendCampaignModal({ campaign, onClose }) {
    const [uids, setUids] = useState('');
    const [emails, setEmails] = useState('');
    const [title, setTitle] = useState(() => firstBlock(campaign.content, 'heading')?.text ?? campaign.name);
    const [body, setBody] = useState(() => plainText(firstBlock(campaign.content, 'text')?.md ?? ''));
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const send = async () => {
        const input = {
            targetUids: parseList(uids),
            targetEmails: parseList(emails),
            title: title.trim(),
            body: body.trim() || null,
        };
        if (input.targetUids.length === 0 && input.targetEmails.length === 0) {
            setError('Add at least one UID or email.');
            return;
        }
        if (!input.title) {
            setError('Title is required.');
            return;
        }
        setSending(true);
        setError(null);
        try {
            const res = await campaignsApi.send(campaign.id, input);
            setResult(res);
            if (res.unresolvedEmails?.length > 0) {
                notification.warning({
                    message: `Sent to ${res.delivered} / ${res.totalRecipients}`,
                    description: `Couldn't resolve: ${res.unresolvedEmails.join(', ')}`,
                });
            } else {
                notification.success({ message: `Sent to ${res.delivered} ${res.delivered === 1 ? 'user' : 'users'}` });
            }
        } catch (e) {
            setError(e.message);
        } finally {
            setSending(false);
        }
    };

    const unresolved = result?.unresolvedEmails ?? [];

    return (
        <Modal
            open
            title={`Send "${campaign.name}" as a notification`}
            okText="Send"
            confirmLoading={sending}
            onOk={send}
            onCancel={onClose}
            width={620}
        >
            <Typography.Paragraph type="secondary">
                Each recipient gets an inbox notification that opens this campaign&rsquo;s dialog. Audience rules and frequency caps do not apply.
            </Typography.Paragraph>
            <Form layout="vertical">
                <Form.Item label="Target UIDs" help="One per line, comma- or space-separated.">
                    <TextArea rows={3} value={uids} onChange={(e) => setUids(e.target.value)} placeholder="abc123uid&#10;def456uid" />
                </Form.Item>
                <Form.Item label="Target emails" help="Resolved server-side. Unmatched ones are reported back.">
                    <TextArea rows={3} value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="alice@example.com&#10;bob@example.com" />
                </Form.Item>
                <Form.Item label="Notification title" required>
                    <Input value={title} maxLength={120} showCount onChange={(e) => setTitle(e.target.value)} />
                </Form.Item>
                <Form.Item label="Notification body">
                    <TextArea rows={2} value={body} maxLength={400} showCount onChange={(e) => setBody(e.target.value)} />
                </Form.Item>
            </Form>
            {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
            {result && (
                <Alert
                    type={unresolved.length > 0 ? 'warning' : 'success'}
                    showIcon
                    message={`Sent to ${result.delivered} / ${result.totalRecipients}`}
                    description={unresolved.length > 0 && (
                        <Space direction="vertical" size={4}>
                            <Typography.Text type="danger">
                                {unresolved.length} email{unresolved.length === 1 ? '' : 's'} couldn&apos;t be matched: {unresolved.join(', ')}
                            </Typography.Text>
                        </Space>
                    )}
                />
            )}
        </Modal>
    );
}
